'use client'
import { useEffect, useState, Component, ErrorInfo, ReactNode } from 'react'
import HeroDesktop from './HeroDesktop'
import HeroMobile from './HeroMobile'

interface HeroProps {
  backgroundImageUrl?: string
  backgroundVideoUrl?: string
  mobileBackgroundImageUrl?: string
  mobileBackgroundVideoUrl?: string
  logoUrl?: string
}

class HeroErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[Hero] Render error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-red-900/20 flex items-center justify-center p-8">
          <div className="bg-red-900/50 p-6 rounded-lg max-w-md">
            <h2 className="text-white text-xl font-bold mb-2">Hero Render Error</h2>
            <p className="text-red-200 text-sm font-mono">{this.state.error?.message || 'Unknown error'}</p>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

export default function Hero(props: HeroProps) {
  const [viewMode, setViewMode] = useState<'desktop' | 'mobile' | 'auto'>('auto')
  const [isPortrait, setIsPortrait] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)

    // Check for forced view mode via URL parameter (?view=desktop or ?view=mobile)
    const urlParams = new URLSearchParams(window.location.search)
    const forcedView = urlParams.get('view')
    if (forcedView === 'desktop' || forcedView === 'mobile') {
      setViewMode(forcedView)
      return // Don't set up media query listener if view is forced
    }

    // Auto mode: use media query
    const mq = window.matchMedia('(orientation: portrait)')
    setIsPortrait(mq.matches)

    const handler = (e: MediaQueryListEvent) => setIsPortrait(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  // Determine which view to show
  const showMobile = viewMode === 'mobile' || (viewMode === 'auto' && isPortrait)

  if (!mounted) {
    return (
      <HeroErrorBoundary>
        <HeroDesktop
          backgroundImageUrl={props.backgroundImageUrl}
          backgroundVideoUrl={props.backgroundVideoUrl}
          logoUrl={props.logoUrl}
        />
      </HeroErrorBoundary>
    )
  }

  return (
    <HeroErrorBoundary>
      {showMobile ? (
        <HeroMobile
          backgroundImageUrl={props.mobileBackgroundImageUrl}
          backgroundVideoUrl={props.mobileBackgroundVideoUrl}
          logoUrl={props.logoUrl}
        />
      ) : (
        <HeroDesktop
          backgroundImageUrl={props.backgroundImageUrl}
          backgroundVideoUrl={props.backgroundVideoUrl}
          logoUrl={props.logoUrl}
        />
      )}
    </HeroErrorBoundary>
  )
}
